<!-- src\components\admin\dashboard\RecentActions.vue -->
<template>
	<div class="admn_recent-actions">
		<!-- Основная карточка -->
		<div class="actions-content">
			<!-- Заголовок -->
			<div class="card-header">
				<h2 class="admn_text-wrapper-22">Журнал действий</h2>
				<div class="header-actions">
					<button 
						class="icon-button" 
						@click="refreshActions" 
						:disabled="loading"
						title="Обновить"
					>
						<svg class="icon" :class="{ spinning: loading }" viewBox="0 0 24 24">
							<path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
						</svg>
					</button>
				</div>
			</div>

			<!-- Список действий -->
			<div class="admn_actions-list" ref="actionsContainer">
				<!-- Загрузка -->
				<div v-if="loading && filteredActions.length === 0" class="state-container">
					<div class="skeleton-loader">
						<div v-for="i in 5" :key="i" class="skeleton-item">
							<div class="skeleton-header">
								<div class="skeleton-avatar"></div>
								<div class="skeleton-text skeleton-text-short"></div>
							</div>
							<div class="skeleton-text skeleton-text-long"></div>
						</div>
					</div>
				</div>

				<!-- Пустое состояние -->
				<div v-else-if="!loading && filteredActions.length === 0" class="state-container">
					<svg class="empty-icon" viewBox="0 0 24 24">
						<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" fill="currentColor"/>
					</svg>
					<p class="empty-text">{{ searchQuery || statusFilter.length ? 'Ничего не найдено' : 'Нет действий' }}</p>
				</div>

				<!-- Список -->
				<div v-else class="actions-scroll">
					<TransitionGroup name="list">
						<div v-for="group in groupedActions" :key="group.date" class="date-group">
							<div class="date-divider">{{ group.label }}</div>
							<div 
								v-for="action in group.actions" 
								:key="action.id" 
								class="action-item"
								@click="openActionDetails(action)"
							>
								<div class="action-layout">
									<div class="user-avatar" :style="{ backgroundColor: getAvatarColor(action.user.name) }">
										{{ getInitials(action.user.name) }}
									</div>
									<div class="action-info">
										<div class="user-name">{{ action.user.name }}</div>
										<p class="action-description" :title="action.description">{{ action.description }}</p>
										<div class="action-meta">
											<span class="time-text">
												<svg class="time-icon" viewBox="0 0 24 24">
													<path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
												</svg>
												{{ action.timeAgo }}
											</span>
											<span class="action-id">{{ formatId(action.id) }}</span>
										</div>
									</div>
									<div class="status-badge" :class="getStatusClass(action.status)">
										{{ getStatusText(action.status) }}
									</div>
								</div>
							</div>
						</div>
					</TransitionGroup>

					<div v-if="loadingMore" class="loading-more">
						<div class="spinner-small"></div>
					</div>
				</div>
			</div>

			<!-- Футер -->
			<div class="card-footer">
				<button class="show-all-btn" @click="toggleShowAll">
					Показать всё
					<svg class="arrow-icon" viewBox="0 0 24 24">
						<path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
					</svg>
				</button>
			</div>
		</div>
	</div>

	<!-- Модальные окна остаются без изменений -->
	<Teleport to="body">
		<Transition name="modal-fade">
			<div v-if="showModal" class="modal-overlay">
				<div class="modal-content">
					<div class="modal-header">
						<h2>Все действия</h2>
						<button class="modal-close" @click="closeModal">
							<svg viewBox="0 0 24 24">
								<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
							</svg>
						</button>
					</div>
					
					<!-- Фильтры в модальном окне -->
					<div class="modal-filters">
						<div class="filters-row">
							<input 
								v-model="modalSearchQuery" 
								type="text" 
								class="modal-search-input"
								placeholder="Поиск по действиям..."
							/>
							<div class="period-filters">
								<button
									v-for="period in periodOptions"
									:key="period.value"
									@click="changeModalPeriod(period.value)"
									:class="['period-chip', { active: modalSelectedPeriod === period.value }]"
								>
									{{ period.label }}
								</button>
							</div>
						</div>
						<div class="status-filters">
							<button
								v-for="status in statusOptions"
								:key="status.value"
								@click="toggleModalStatusFilter(status.value)"
								:class="['status-chip', status.class, { active: modalStatusFilter.includes(status.value) }]"
							>
								{{ status.label }}
							</button>
						</div>
					</div>
					
					<div class="modal-body" @scroll="handleModalScroll">
						<TransitionGroup v-if="!loadingModalActions && filteredModalActions.length > 0" name="list" tag="div">
							<div 
								v-for="action in filteredModalActions" 
								:key="action.id" 
								class="modal-action-item"
							>
								<div class="user-avatar" :style="{ backgroundColor: getAvatarColor(action.user.name) }">
									{{ getInitials(action.user.name) }}
								</div>
								<div class="action-details">
									<div class="action-header-modal">
										<span class="user-name">{{ action.user.name }}</span>
										<span :class="['status-badge', getStatusClass(action.status)]">
											{{ getStatusText(action.status) }}
										</span>
									</div>
									<p class="action-description">{{ action.description }}</p>
									<span class="action-time">{{ action.timeAgo }}</span>
								</div>
							</div>
						</TransitionGroup>
						<div v-if="loadingMore" class="loading-more">
							<div class="spinner-small"></div>
						</div>
						<div v-else-if="loadingModalActions" class="state-container">
							<div class="spinner"></div>
						</div>
						<div v-else-if="filteredModalActions.length === 0" class="state-container">
							<p class="empty-text">Нет данных</p>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getRecentActions } from '@/services/dashboard'

// State
const actions = ref([])
const allActions = ref([])
const loading = ref(true)
const loadingMore = ref(false)
const loadingModalActions = ref(false)
const showModal = ref(false)
const selectedAction = ref(null)

const searchQuery = ref('')
const statusFilter = ref([])
const modalSearchQuery = ref('')
const modalStatusFilter = ref([])
const selectedPeriod = ref('1d')
const modalSelectedPeriod = ref('30d')

const pagination = ref({ total: 0, offset: 0, hasMore: false })
const modalPagination = ref({ total: 0, offset: 0, hasMore: false })

let autoRefreshInterval = null

const statusOptions = [
	{ value: 'success', label: 'Выполнено', class: 'status-success' },
	{ value: 'warning', label: 'Внимание', class: 'status-warning' },
	{ value: 'info', label: 'Информация', class: 'status-info' },
	{ value: 'error', label: 'Ошибка', class: 'status-error' }
]

const periodOptions = [
	{ value: '1d', label: 'День' },
	{ value: '7d', label: 'Неделя' },
	{ value: '30d', label: 'Месяц' }
]

// Computed
const filteredActions = computed(() => {
	let result = actions.value

	if (searchQuery.value) {
		const query = searchQuery.value.toLowerCase()
		result = result.filter(a => 
			a.description.toLowerCase().includes(query) ||
			a.user.name.toLowerCase().includes(query)
		)
	}

	if (statusFilter.value.length > 0) {
		result = result.filter(a => statusFilter.value.includes(a.status))
	}

	return result.slice(0, 7)
})

const filteredModalActions = computed(() => {
	let result = allActions.value

	if (modalSearchQuery.value) {
		const query = modalSearchQuery.value.toLowerCase()
		result = result.filter(a => 
			a.description.toLowerCase().includes(query) ||
			a.user.name.toLowerCase().includes(query)
		)
	}

	if (modalStatusFilter.value.length > 0) {
		result = result.filter(a => modalStatusFilter.value.includes(a.status))
	}

	return result
})

const groupedActions = computed(() => {
	const groups = {}
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const yesterday = new Date(today)
	yesterday.setDate(yesterday.getDate() - 1)

	filteredActions.value.forEach(action => {
		const actionDate = new Date(action.timestamp || Date.now())
		const actionDay = new Date(actionDate.getFullYear(), actionDate.getMonth(), actionDate.getDate())
		
		let label
		if (actionDay.getTime() === today.getTime()) {
			label = 'Сегодня'
		} else if (actionDay.getTime() === yesterday.getTime()) {
			label = 'Вчера'
		} else {
			label = actionDay.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
		}

		if (!groups[label]) {
			groups[label] = { date: label, label, actions: [] }
		}
		groups[label].actions.push(action)
	})

	return Object.values(groups)
})

// Methods
const fetchActions = async (loadMore = false) => {
	try {
		if (loadMore) loadingMore.value = true
		else loading.value = true

		const offset = loadMore ? pagination.value.offset : 0
		const response = await getRecentActions({ limit: 10, offset, period: selectedPeriod.value })
		const data = response.data || response
		const newActions = data.actions || []

		if (loadMore) {
			actions.value = [...actions.value, ...newActions]
		} else {
			actions.value = newActions
		}

		pagination.value = data.pagination || { total: 0, offset: offset + newActions.length, hasMore: false }
	} catch (error) {
		console.error('[RecentActions] Ошибка:', error)
	} finally {
		loading.value = false
		loadingMore.value = false
	}
}

const fetchAllActions = async (loadMore = false) => {
	try {
		if (loadMore) loadingMore.value = true
		else loadingModalActions.value = true

		const offset = loadMore ? modalPagination.value.offset : 0
		const response = await getRecentActions({ limit: 20, offset, period: modalSelectedPeriod.value })
		const data = response.data || response
		const newActions = data.actions || []

		if (loadMore) {
			allActions.value = [...allActions.value, ...newActions]
		} else {
			allActions.value = newActions
		}

		modalPagination.value = {
			total: data.pagination?.total || 0,
			offset: offset + newActions.length,
			hasMore: data.pagination?.hasMore || false
		}
	} catch (error) {
		console.error('Ошибка загрузки всех действий:', error)
	} finally {
		loadingModalActions.value = false
		loadingMore.value = false
	}
}

const changeModalPeriod = (period) => {
	modalSelectedPeriod.value = period
	allActions.value = []
	modalPagination.value = { total: 0, offset: 0, hasMore: false }
	fetchAllActions(false)
}

const handleModalScroll = (event) => {
	const { scrollTop, scrollHeight, clientHeight } = event.target
	const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
	
	if (scrollPercentage > 0.8 && !loadingMore.value && modalPagination.value.hasMore) {
		fetchAllActions(true)
	}
}

const refreshActions = async () => {
	await fetchActions(false)
}

const _toggleFilters = () => {
	showFilters.value = !showFilters.value
}

const toggleModalStatusFilter = (status) => {
	const index = modalStatusFilter.value.indexOf(status)
	if (index > -1) {
		modalStatusFilter.value.splice(index, 1)
	} else {
		modalStatusFilter.value.push(status)
	}
}

const toggleShowAll = () => {
	showModal.value = true
	document.body.style.overflow = 'hidden'
	console.log('[RecentActions] Открытие модального окна, период:', modalSelectedPeriod.value)
	if (allActions.value.length === 0) {
		fetchAllActions()
	}
}

const closeModal = () => {
	showModal.value = false
	document.body.style.overflow = ''
	modalSearchQuery.value = ''
	modalStatusFilter.value = []
}

const openActionDetails = (action) => {
	selectedAction.value = action
}

const formatId = (id) => String(id).padStart(8, '0')

const getStatusClass = (status) => ({
	success: 'status-success',
	warning: 'status-warning',
	info: 'status-info',
	error: 'status-error'
}[status] || 'status-info')

const getStatusText = (status) => ({
	success: 'Выполнено',
	warning: 'Внимание',
	info: 'Информация',
	error: 'Ошибка'
}[status] || 'Информация')

const getInitials = (name) => {
	return name
		.split(' ')
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)
}

const getAvatarColor = (name) => {
	const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#6C5CE7', '#A29BFE', '#FD79A8']
	const charCode = name.charCodeAt(0) + name.charCodeAt(name.length - 1)
	return colors[charCode % colors.length]
}

// Lifecycle
onMounted(() => {
	fetchActions()
	autoRefreshInterval = setInterval(() => fetchActions(false), 30000)
})

onUnmounted(() => {
	if (autoRefreshInterval) clearInterval(autoRefreshInterval)
	document.body.style.overflow = ''
})
</script>

<style scoped>
.admn_recent-actions {
	background-color: #ffffff;
	border-radius: 12px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	min-width: 0;
	overflow: hidden;
}

.actions-content {
	display: flex;
	flex-direction: column;
	height: 100%;
}

/* Header */
.card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 14px 16px 10px;
	border-bottom: 1px solid #f0f0f0;
	flex-shrink: 0;
}

.admn_text-wrapper-22 {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0;
}

.header-actions {
	display: flex;
	gap: 8px;
}

.icon-button {
	width: 36px;
	height: 36px;
	border: none;
	background: #f5f5f5;
	border-radius: 8px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	color: #666;
}

.icon-button:hover:not(:disabled) {
	background: #e8e8e8;
	color: #2563ea;
}

.icon-button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.icon-button.active {
	background: #2563ea;
	color: white;
}

.icon {
	width: 20px;
	height: 20px;
}

.spinning {
	animation: spin 1s linear infinite;
}

/* Filters */
.filters-panel {
	padding: 12px 16px;
	background: #fafafa;
	border-bottom: 1px solid #f0f0f0;
}

.filters-row {
	display: flex;
	gap: 12px;
	align-items: center;
	margin-bottom: 12px;
	flex-wrap: wrap;
}

.search-input {
	flex: 1;
	min-width: 150px;
	padding: 8px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	font-size: 14px;
	font-family: 'Poppins', Helvetica, sans-serif;
	box-sizing: border-box;
}

.search-input:focus {
	outline: none;
	border-color: #2563ea;
	box-shadow: 0 0 0 3px rgba(37, 99, 234, 0.1);
}

.status-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.status-chip {
	padding: 6px 12px;
	border: 1.5px solid transparent;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	background: white;
	font-family: 'Poppins', Helvetica, sans-serif;
}

.status-chip.status-success {
	color: #22c55e;
	background: #f0fdf4;
}

.status-chip.status-success.active {
	border-color: #22c55e;
	background: #dcfce7;
}

.status-chip.status-warning {
	color: #f59e0b;
	background: #fffbeb;
}

.status-chip.status-warning.active {
	border-color: #f59e0b;
	background: #fef3c7;
}

.status-chip.status-info {
	color: #2563ea;
	background: #eff6ff;
}

.status-chip.status-info.active {
	border-color: #2563ea;
	background: #dbeafe;
}

.status-chip.status-error {
	color: #ef4444;
	background: #fef2f2;
}

.status-chip.status-error.active {
	border-color: #ef4444;
	background: #fee2e2;
}

/* Period filters */
.period-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	flex: 1;
	min-width: 0;
}

.period-chip {
	padding: 6px 14px;
	border: 1.5px solid #e0e0e0;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	background: white;
	color: #666;
	font-family: 'Poppins', Helvetica, sans-serif;
}

.period-chip:hover {
	border-color: #2563ea;
	color: #2563ea;
	background: #eff6ff;
}

.period-chip.active {
	border-color: #2563ea;
	background: #2563ea;
	color: white;
}

/* Actions List */
.admn_actions-list {
	flex: 1;
	overflow: hidden;
	padding: 10px 16px;
	min-height: 0;
}

.actions-scroll {
	height: 100%;
	overflow-y: auto;
	padding-right: 8px;
}

.state-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	color: #999;
}

.empty-icon {
	width: 48px;
	height: 48px;
	color: #ddd;
	margin-bottom: 12px;
}

.empty-text {
	font-size: 14px;
	color: #999;
	margin: 0;
}

/* Date groups */
.date-group {
	margin-bottom: 20px;
}

.date-divider {
	font-size: 11px;
	font-weight: 600;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid #f0f0f0;
}

/* Action item */
.action-item {
	padding: 10px 12px;
	border-radius: 10px;
	background: #fafafa;
	cursor: pointer;
	transition: all 0.2s ease;
	margin-bottom: 8px;
}

.action-item:hover {
	background: #f0f0f0;
	transform: translateX(4px);
}

.action-layout {
	display: flex;
	gap: 10px;
	align-items: flex-start;
}

.user-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: 600;
	font-size: 12px;
	flex-shrink: 0;
}

.action-info {
	flex: 1;
	min-width: 0;
}

.user-name {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #1a1a1a;
	margin-bottom: 4px;
}

.action-description {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 13px;
	color: #666;
	margin: 0 0 8px 0;
	line-height: 1.4;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.action-meta {
	display: flex;
	align-items: center;
	gap: 12px;
}

.time-text {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: #999;
}

.time-icon {
	width: 14px;
	height: 14px;
}

.action-id {
	font-family: 'Courier New', monospace;
	font-size: 11px;
	color: #bbb;
}

/* Status badge */
.status-badge {
	padding: 5px 10px;
	border-radius: 6px;
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
}

.status-success {
	background-color: #dcfce7;
	color: #16a34a;
}

.status-warning {
	background-color: #fef3c7;
	color: #d97706;
}

.status-info {
	background-color: #dbeafe;
	color: #2563ea;
}

.status-error {
	background-color: #fee2e2;
	color: #dc2626;
}

/* Footer */
.card-footer {
	padding: 10px 16px;
	border-top: 1px solid #f0f0f0;
	display: flex;
	justify-content: flex-end;
	flex-shrink: 0;
}

.show-all-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	background: none;
	border: none;
	color: #2563ea;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	padding: 8px 12px;
	border-radius: 8px;
	transition: all 0.2s ease;
}

.show-all-btn:hover {
	background: #eff6ff;
}

.arrow-icon {
	width: 18px;
	height: 18px;
	transition: transform 0.2s ease;
}

.show-all-btn:hover .arrow-icon {
	transform: translateX(3px);
}

/* Skeleton */
.skeleton-loader {
	width: 100%;
}

.skeleton-item {
	padding: 14px;
	margin-bottom: 12px;
}

.skeleton-header {
	display: flex;
	align-items: center;
	gap: 14px;
	margin-bottom: 12px;
}

.skeleton-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 200% 100%;
	animation: skeleton-loading 1.5s infinite;
}

.skeleton-text {
	height: 12px;
	border-radius: 6px;
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 200% 100%;
	animation: skeleton-loading 1.5s infinite;
}

.skeleton-text-short {
	width: 120px;
}

.skeleton-text-long {
	width: 100%;
}

/* Loading */
.loading-more {
	display: flex;
	justify-content: center;
	padding: 20px;
}

.spinner-small {
	width: 24px;
	height: 24px;
	border: 2px solid #f0f0f0;
	border-top-color: #2563ea;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 3px solid #f0f0f0;
	border-top-color: #2563ea;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

/* Modal */
.modal-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.6);
	backdrop-filter: blur(4px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 20px;
}

.modal-content {
	background: white;
	border-radius: 16px;
	width: 100%;
	max-width: 700px;
	max-height: 80vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
	overflow: hidden;
	box-sizing: border-box;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px 24px;
	border-bottom: 1px solid #f0f0f0;
}

.modal-header h2 {
	margin: 0;
	font-size: 20px;
	font-weight: 600;
}

.modal-filters {
	padding: 16px 24px;
	background: #fafafa;
	border-bottom: 1px solid #f0f0f0;
	box-sizing: border-box;
	width: 100%;
	flex-shrink: 0;
}

.modal-filters .search-input {
	margin-bottom: 12px;
	width: 100% !important;
	max-width: 100% !important;
	min-width: 0 !important;
	box-sizing: border-box !important;
	display: block;
}

.modal-close {
	width: 36px;
	height: 36px;
	border: none;
	background: #f5f5f5;
	border-radius: 8px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #666;
	transition: all 0.2s ease;
}

.modal-close:hover {
	background: #fee2e2;
	color: #dc2626;
}

.modal-close svg {
	width: 20px;
	height: 20px;
}

.modal-body {
	flex: 1;
	overflow-y: auto;
	padding: 20px 24px;
}

.modal-action-item {
	display: flex;
	gap: 14px;
	padding: 16px;
	margin-bottom: 12px;
	border-radius: 12px;
	background: #fafafa;
	transition: all 0.2s ease;
}

.modal-action-item:hover {
	background: #f0f0f0;
}

.action-details {
	flex: 1;
	min-width: 0;
}

.action-header-modal {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 8px;
	flex-wrap: wrap;
}

.action-time {
	font-size: 12px;
	color: #999;
}

/* Animations */
@keyframes spin {
	to { transform: rotate(360deg); }
}

@keyframes skeleton-loading {
	0% { background-position: 200% 0; }
	100% { background-position: -200% 0; }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateY(-10px);
	opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

.modal-fade-enter-from .modal-content,
.modal-fade-leave-to .modal-content {
	transform: scale(0.95) translateY(20px);
}

.list-enter-active,
.list-leave-active {
	transition: all 0.3s ease;
}

.list-enter-from {
	opacity: 0;
	transform: translateX(-15px);
}

.list-leave-to {
	opacity: 0;
	transform: translateX(15px);
}

/* Scrollbar */
.actions-scroll::-webkit-scrollbar,
.modal-body::-webkit-scrollbar {
	width: 6px;
}

.actions-scroll::-webkit-scrollbar-track,
.modal-body::-webkit-scrollbar-track {
	background: transparent;
}

.actions-scroll::-webkit-scrollbar-thumb,
.modal-body::-webkit-scrollbar-thumb {
	background: #ddd;
	border-radius: 3px;
}

.actions-scroll::-webkit-scrollbar-thumb:hover,
.modal-body::-webkit-scrollbar-thumb:hover {
	background: #ccc;
}
</style>

<!-- Глобальные стили для модального окна (работают с Teleport) -->
<style>
.modal-overlay .modal-filters {
	padding: 16px 24px;
	background: #fafafa;
	border-bottom: 1px solid #f0f0f0;
	box-sizing: border-box;
	width: 100%;
	flex-shrink: 0;
}

.modal-overlay .filters-row {
	display: flex;
	gap: 12px;
	align-items: center;
	margin-bottom: 12px;
	flex-wrap: wrap;
}

.modal-overlay .modal-filters .modal-search-input {
	flex: 0 0 200px;
	box-sizing: border-box;
	display: block;
	padding: 8px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	font-size: 14px;
	font-family: 'Poppins', Helvetica, sans-serif;
}

.modal-overlay .modal-filters .modal-search-input:focus {
	outline: none;
	border-color: #2563ea;
	box-shadow: 0 0 0 3px rgba(37, 99, 234, 0.1);
}

.modal-overlay .status-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.modal-overlay .period-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	flex: 1;
	min-width: 0;
}
</style>

